import { useState, useCallback, useEffect } from "react";

interface ViewModeState {
  viewMode: "table" | "grouped" | "cards" | "board";
  setViewMode: (mode: "table" | "grouped" | "cards" | "board") => void;
}

/**
 * Manages view mode (table/grouped/cards/board) with localStorage persistence
 * Initializes from localStorage on mount and persists changes
 */
export function useViewModeState(entityName: string): ViewModeState {
  const storageKey = `${entityName}-view-mode`;
  const [viewMode, setViewModeState] = useState<"table" | "grouped" | "cards" | "board">("table");

  // Initialize view mode from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedViewMode = localStorage.getItem(storageKey) as "table" | "grouped" | "cards" | "board" | null;
      if (savedViewMode && ["table", "grouped", "cards", "board"].includes(savedViewMode)) {
        setViewModeState(savedViewMode);
      }
    }
  }, [storageKey]);

  // Persist view mode to localStorage when it changes
  const setViewMode = useCallback(
    (mode: "table" | "grouped" | "cards" | "board") => {
      setViewModeState(mode);
      if (typeof window !== "undefined") {
        localStorage.setItem(storageKey, mode);
      }
    },
    [storageKey]
  );

  return {
    viewMode,
    setViewMode,
  };
}
